import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UsersThree, Coins, GlobeHemisphereWest, CaretRight } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * FeaturedRoomsStrip — a compact horizontally-scrolling row that surfaces the
 * top active public + paid rooms directly on the Home feed. Rendered as an
 * absolute overlay so it doesn't disturb the vertical snap-scroll of PostCards.
 *
 * Auto-hides when the API returns zero active rooms (never occupies space in
 * the "no rooms yet" case). Refreshes every 45s to keep counts fresh.
 */
export default function FeaturedRoomsStrip() {
  const [rooms, setRooms] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/rooms/featured", { params: { limit: 4 } });
        if (alive) setRooms(data || []);
      } catch (e) { /* silent */ }
    };
    load();
    const t = setInterval(load, 45_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (dismissed || rooms.length === 0) return null;

  return (
    <div
      className="absolute left-0 right-0 top-[68px] z-20 px-4 pointer-events-none"
      data-testid="featured-rooms-strip"
    >
      <div className="pointer-events-auto">
        <div className="flex items-center justify-between mb-1.5 px-1">
          <p className="text-papaya text-[9px] tracking-[0.35em] uppercase font-black flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live in Rooms
          </p>
          <button
            onClick={() => setDismissed(true)}
            className="text-cream/40 hover:text-cream/70 text-[9px] tracking-[0.3em] uppercase font-bold"
            data-testid="featured-rooms-dismiss"
          >Hide</button>
        </div>
        <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1">
          {rooms.map((r) => (
            <FeaturedRoomCard key={r.id} room={r} />
          ))}
          <Link
            to="/rooms"
            className="flex-shrink-0 snap-start rounded-2xl crystal-glass px-4 py-2 flex items-center gap-1.5 text-cream/70 hover:text-papaya hover:border-papaya/30 transition-colors"
            data-testid="featured-rooms-see-all"
          >
            <span className="text-[10px] tracking-[0.25em] uppercase font-black">See all</span>
            <CaretRight size={12} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function FeaturedRoomCard({ room }) {
  const isPaid = room.room_type === "paid";
  return (
    <Link
      to={`/rooms/${room.id}`}
      className="flex-shrink-0 snap-start w-[220px] rounded-2xl crystal-glass px-3 py-2 hover:border-papaya/40 transition-colors block"
      data-testid={`featured-room-${room.id}`}
    >
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isPaid ? "bg-jam-gold/20 text-jam-gold" : "bg-teal-500/20 text-teal-300"
        }`}>
          {isPaid ? <Coins size={16} weight="fill" /> : <GlobeHemisphereWest size={16} weight="fill" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-cream font-bold text-xs truncate leading-tight">{room.title}</p>
          <p className="text-cream/50 text-[10px] truncate">
            @{room.host_username || room.host_display_name}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-cream/60 text-[9px] tracking-[0.25em] uppercase font-bold flex items-center gap-1">
          <UsersThree size={10} weight="fill" />
          {room.participants_count || 0}/{room.max_participants || 12}
        </span>
        {isPaid && (
          <span className="text-jam-gold text-[9px] tracking-[0.25em] uppercase font-black">
            {room.price_waves}w entry
          </span>
        )}
      </div>
    </Link>
  );
}
